import { matchPath } from 'react-router';
import { appLocales } from 'features/i18n/locales';
import { getLinkLocale } from 'features/i18n/utils/getLinkLocale';
import { RoutePath } from 'features/routing/constants';
import type { AppLocales } from '@hub/common';

export const START_FLOW_STORAGE_KEY = 'ph_start_flow';

export type StartFlowState = { locale?: AppLocales };

const isStartFlowState = (value: unknown): value is StartFlowState => {
  if (typeof value !== 'object' || value === null) return false;
  const { locale } = value as Record<string, unknown>;
  return locale === undefined || appLocales.includes(locale as AppLocales);
};

const ignoringBlockedStorage = (write: () => void): void => {
  try {
    write();
  } catch {
    // Blocked storage means no flow.
  }
};

const writeStartFlow = (state: StartFlowState): void => {
  ignoringBlockedStorage(() => {
    sessionStorage.setItem(START_FLOW_STORAGE_KEY, JSON.stringify(state));
  });
};

export const endStartFlow = (): void => {
  ignoringBlockedStorage(() => {
    sessionStorage.removeItem(START_FLOW_STORAGE_KEY);
  });
};

/** Runs once per document load: a start link begins the flow, the Identity
 *  callback keeps it, any other page ends it. */
export const syncStartFlowWithPage = (location: {
  pathname: string;
  search: string;
}): void => {
  if (matchPath(RoutePath.ProtocolStart, location.pathname)) {
    const locale = getLinkLocale(location.search);
    writeStartFlow(locale ? { locale } : {});
    return;
  }
  if (!matchPath(RoutePath.AuthCallback, location.pathname)) endStartFlow();
};

export const readStartFlow = (): StartFlowState | undefined => {
  try {
    const raw = sessionStorage.getItem(START_FLOW_STORAGE_KEY);
    if (raw === null) return undefined;
    const parsed: unknown = JSON.parse(raw);
    return isStartFlowState(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
};

export const setStartFlowLocale = (locale: AppLocales): void => {
  const current = readStartFlow();
  if (!current || current.locale !== undefined) return;
  writeStartFlow({ ...current, locale });
};
